<div class="queue-container">
  
  <!-- Header -->
  <div class="queue-header">
    <div class="header-title">
      <mat-icon>queue_music</mat-icon>
      <h2>Queue</h2>
    </div>

    <div class="queue-stats" *ngIf="isPlayerReady && nextTracks.length > 0">
      <span class="track-count">{{ getQueueTrackCount() }} tracks</span>
      <span class="separator">•</span>
      <span class="duration">{{ formatQueueDuration() }}</span>
    </div>

    <div class="header-actions">
      <button mat-icon-button 
              matTooltip="Save queue as playlist"
              [disabled]="!nextTracks.length"
              (click)="saveQueueAsPlaylist()">
        <mat-icon>playlist_add</mat-icon>
      </button>

      <button mat-icon-button 
              matTooltip="Share queue"
              [disabled]="!nextTracks.length"
              (click)="shareQueue()">
        <mat-icon>share</mat-icon>
      </button>

      <button mat-icon-button 
              matTooltip="Clear queue"
              [disabled]="!nextTracks.length"
              (click)="clearQueue()">
        <mat-icon>clear_all</mat-icon>
      </button>
    </div>
  </div>

  <!-- No Premium Notice -->
  <div *ngIf="!isPremium" class="no-premium">
    <mat-icon>info</mat-icon>
    <h3>Spotify Premium Required</h3>
    <p>Queue functionality requires a Spotify Premium subscription.</p>
  </div>

  <!-- Player Not Ready -->
  <div *ngIf="isPremium && !isPlayerReady" class="not-ready">
    <mat-icon>music_off</mat-icon>
    <h3>Player Not Available</h3>
    <p>Spotify player is not currently available.</p>
  </div>

  <!-- No Current Track -->
  <div *ngIf="isPremium && isPlayerReady && !currentTrack" class="no-current-track">
    <mat-icon>music_note</mat-icon>
    <h3>No Track Playing</h3>
    <p>Start playing music to see the queue.</p>
  </div>

  <!-- Queue Content -->
  <div *ngIf="isPremium && isPlayerReady && currentTrack" class="queue-content">
    
    <!-- Now Playing Section -->
    <div class="now-playing-section">
      <h3>Now Playing</h3>
      <div class="current-track">
        <div class="track-art">
          <img *ngIf="getAlbumImage(currentTrack); else noImage" 
               [src]="getAlbumImage(currentTrack)" 
               [alt]="currentTrack.album.name"
               class="album-image">
          <ng-template #noImage>
            <mat-icon class="no-image-icon">music_note</mat-icon>
          </ng-template>
        </div>

        <div class="track-info">
          <div class="track-name" [title]="currentTrack.name">
            {{ currentTrack.name }}
          </div>
          <div class="artist-name" [title]="getTrackArtists(currentTrack)">
            {{ getTrackArtists(currentTrack) }}
          </div>
        </div>

        <div class="track-duration">
          {{ formatDuration(currentTrack.duration_ms) }}
        </div>

        <div class="playing-indicator">
          <mat-icon class="playing-icon">graphic_eq</mat-icon>
        </div>
      </div>
    </div>

    <!-- Up Next Section -->
    <div class="up-next-section">
      <div class="section-header">
        <h3>Up Next</h3>
        <span *ngIf="nextTracks.length > 0" class="track-count">
          {{ nextTracks.length }} track{{ nextTracks.length !== 1 ? 's' : '' }}
        </span>
      </div>

      <!-- Queue List -->
      <div class="queue-list" 
           cdkDropList 
           (cdkDropListDropped)="onQueueDrop($event)"
           [cdkDropListDisabled]="isReordering">
        
        <div *ngFor="let track of nextTracks; let i = index; trackBy: trackByUri" 
             class="queue-item"
             cdkDrag
             [cdkDragDisabled]="isReordering">
          
          <!-- Drag Handle -->
          <div class="drag-handle" cdkDragHandle>
            <mat-icon>drag_handle</mat-icon>
          </div>

          <!-- Track Position -->
          <div class="track-position">{{ i + 1 }}</div>

          <!-- Track Art -->
          <div class="track-art">
            <img *ngIf="getAlbumImage(track); else noTrackImage" 
                 [src]="getAlbumImage(track)" 
                 [alt]="track.album.name"
                 class="album-image">
            <ng-template #noTrackImage>
              <mat-icon class="no-image-icon">music_note</mat-icon>
            </ng-template>
          </div>

          <!-- Track Info -->
          <div class="track-info">
            <div class="track-name" [title]="track.name">
              {{ track.name }}
            </div>
            <div class="artist-name" [title]="getTrackArtists(track)">
              {{ getTrackArtists(track) }}
            </div>
          </div>

          <!-- Track Duration -->
          <div class="track-duration">
            {{ formatDuration(track.duration_ms) }}
          </div>

          <!-- Actions -->
          <div class="track-actions">
            <button mat-icon-button 
                    matTooltip="Play now"
                    (click)="playTrackAtIndex(i)">
              <mat-icon>play_arrow</mat-icon>
            </button>

            <button mat-icon-button 
                    matTooltip="Remove from queue"
                    (click)="removeFromQueue(i)">
              <mat-icon>remove</mat-icon>
            </button>
          </div>

          <!-- CDK Drag Preview -->
          <div class="queue-item-preview" *cdkDragPreview>
            <div class="preview-content">
              <mat-icon>music_note</mat-icon>
              <div class="preview-info">
                <div class="preview-name">{{ track.name }}</div>
                <div class="preview-artist">{{ getTrackArtists(track) }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty Queue State -->
        <div *ngIf="nextTracks.length === 0" class="empty-queue">
          <mat-icon>queue_music</mat-icon>
          <h4>Queue is empty</h4>
          <p>Add songs to see them here next</p>
        </div>
      </div>
    </div>

    <!-- Previously Played Section -->
    <div class="previous-section" *ngIf="previousTracks.length > 0">
      <div class="section-header">
        <h3>
          <button mat-button 
                  class="section-toggle"
                  (click)="togglePreviousTracks()">
            <mat-icon>{{ showPreviousTracks ? 'expand_less' : 'expand_more' }}</mat-icon>
            Recently Played
          </button>
        </h3>
        <span class="track-count">
          {{ previousTracks.length }} track{{ previousTracks.length !== 1 ? 's' : '' }}
        </span>
      </div>

      <div class="previous-list" [class.expanded]="showPreviousTracks">
        <div *ngFor="let track of previousTracks; let i = index" 
             class="previous-item">
          
          <!-- Track Position -->
          <div class="track-position">{{ previousTracks.length - i }}</div>

          <!-- Track Art -->
          <div class="track-art">
            <img *ngIf="getAlbumImage(track); else noPrevImage" 
                 [src]="getAlbumImage(track)" 
                 [alt]="track.album.name"
                 class="album-image">
            <ng-template #noPrevImage>
              <mat-icon class="no-image-icon">music_note</mat-icon>
            </ng-template>
          </div>

          <!-- Track Info -->
          <div class="track-info">
            <div class="track-name" [title]="track.name">
              {{ track.name }}
            </div>
            <div class="artist-name" [title]="getTrackArtists(track)">
              {{ getTrackArtists(track) }}
            </div>
          </div>

          <!-- Track Duration -->
          <div class="track-duration">
            {{ formatDuration(track.duration_ms) }}
          </div>

          <!-- Actions -->
          <div class="track-actions">
            <button mat-icon-button 
                    matTooltip="Play again"
                    (click)="addToQueue(track.uri)">
              <mat-icon>replay</mat-icon>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>